import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { SkeletonRows } from '@/components/ui/skeleton'
import { type DriverMvrState, isSelectable, useMvrEligibility, useRequestMvr } from '../../models/mvr'
import { ConfirmRepullDialog } from './ConfirmRepullDialog'
import { MvrDriverPicker } from './MvrDriverPicker'

function summary(states: DriverMvrState[], selectedIds: Set<string>): string {
  const selected = states.filter((s) => selectedIds.has(s.driverId))
  const current = selected.filter((s) => s.isCurrent).length
  if (selected.length === 0) return 'No drivers selected'
  const base = `${selected.length} ${selected.length === 1 ? 'record' : 'records'} will be pulled`
  // Every pull is charged, so say plainly what is about to be spent twice.
  return current > 0 ? `${base}. ${current} already current` : base
}

export function RequestMvrDialog({ orgId, children }: { orgId: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const [confirmingRepull, setConfirmingRepull] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const { states, isLoading } = useMvrEligibility(orgId, open)
  const requestMvr = useRequestMvr(orgId)

  const selectable = useMemo(() => states.filter(isSelectable), [states])
  const freshSelected = useMemo(
    () => states.filter((s) => selectedIds.has(s.driverId) && s.isCurrent),
    [states, selectedIds]
  )

  function toggle(driverId: string) {
    setSelectedIds((previous) => {
      const next = new Set(previous)
      if (next.has(driverId)) next.delete(driverId)
      else next.add(driverId)
      return next
    })
  }

  function toggleAll() {
    setSelectedIds((previous) =>
      previous.size === selectable.length ? new Set() : new Set(selectable.map((s) => s.driverId))
    )
  }

  function onOpenChange(next: boolean) {
    setOpen(next)
    if (!next) {
      setSelectedIds(new Set())
      setConfirmingRepull(false)
    }
  }

  function submit() {
    // force: true only when the user went through the "pull again" confirmation --
    // otherwise the backend's own freshness dedup still applies.
    requestMvr.mutate(
      { driverIds: [...selectedIds], force: freshSelected.length > 0 },
      {
        onSuccess: (result) => {
          const queued = result.requests.length
          toast.success(`${queued} MVR ${queued === 1 ? 'request' : 'requests'} queued`)
          onOpenChange(false)
        },
        onError: () => toast.error('Could not request MVRs'),
      }
    )
  }

  function handleConfirm() {
    // Each pull is billed, so a still-current record needs an explicit go-ahead.
    if (freshSelected.length > 0) {
      setConfirmingRepull(true)
      return
    }
    submit()
  }

  const blocked = states.filter((s) => !s.eligible)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>Request MVR</DialogTitle>
          <DialogDescription>
            Records are pulled from Checkr one driver at a time, so a large fleet takes a while. Each
            driver&apos;s row updates as their record arrives.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <SkeletonRows />
        ) : states.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-sm">This customer has no drivers yet.</p>
        ) : (
          <>
            <MvrDriverPicker
              states={states}
              selectedIds={selectedIds}
              onToggle={toggle}
              onToggleAll={toggleAll}
            />
            {blocked.length > 0 && (
              <p className="text-muted-foreground text-xs">
                {blocked.length} {blocked.length === 1 ? 'driver' : 'drivers'} cannot be pulled until their
                details are complete. Fix them on the Drivers tab.
              </p>
            )}
          </>
        )}

        <div className="flex items-center justify-between gap-3 pt-2">
          <span className="text-muted-foreground text-sm">{summary(states, selectedIds)}</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedIds.size === 0}
              loading={requestMvr.isPending ? 'Requesting…' : undefined}
            >
              Request MVR
            </Button>
          </div>
        </div>
        <ConfirmRepullDialog
          drivers={freshSelected}
          open={confirmingRepull}
          onOpenChange={setConfirmingRepull}
          onConfirm={submit}
          pending={requestMvr.isPending}
        />
      </DialogContent>
    </Dialog>
  )
}
